import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertCircle } from "lucide-react";
import { useMemo } from "react";
import { hooks as h } from "@/hooks";
import { Alert, AlertDescription } from "@patient-watch/ui/alert";
import Stepper from "@/components/common/Stepper";
import useUsersOrganisationsRolesQuery from "@/features/auth/api/selectUsersOrganisationsRoles";
import { uniqueOrganisationsFromRoles } from "@/features/auth/utils/uniqueOrganisationsFromRoles";
import {
    type DiaryInviteSearchT,
    getDiaryInviteContext,
    getDiaryInviteOrigin,
} from "@/features/invite/utils/diaryInviteContext";
import { getDiaryInviteSteps, parseDiaryInviteMethod } from "@/features/invite/utils/diaryInviteSteps";
import InvitePatientForm from "@/features/templates/features/invite-patient/InvitePatientForm";
import { normalizeTemplateSettingsSearch } from "@/features/templates/utils/normalizeTemplateSettingsSearch";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { useInviteCopy } from "@/features/i18n/inviteCopy";
import { usePatientsCopy } from "@/features/i18n/patientsCopy";
import { useTemplatesCopy } from "@/features/i18n/templatesCopy";
import { type QuickstartDataSchemaT } from "@patient-watch/types";

export const Route = createFileRoute("/_app/invite/diary/patient")({
    component: InvitePatientPage,
    validateSearch: (search: Record<string, unknown>) => search as DiaryInviteSearchT,
});

function InvitePatientPage() {
    const search = Route.useSearch();
    const { auth } = Route.useRouteContext();
    const navigate = useNavigate();
    const { locale: appLocale } = useAppLocale();
    const inviteCopy = useInviteCopy();
    const patientsCopy = usePatientsCopy();
    const templatesCopy = useTemplatesCopy();

    const origin = getDiaryInviteOrigin(search);
    const inviteMethod = parseDiaryInviteMethod(search.invite_method) ?? "email";
    const context = getDiaryInviteContext(origin, {
        patients: patientsCopy.patients,
        templates: templatesCopy.templates,
        newDiary: patientsCopy.newDiary,
    });

    const { data: userOrgRoles = [], isLoading: isLoadingOrgs } = useUsersOrganisationsRolesQuery(auth.id);
    const organisations = useMemo(() => uniqueOrganisationsFromRoles(userOrgRoles), [userOrgRoles]);

    h.useNav([
        { label: context.rootLabel, route: { to: context.rootTo } },
        { label: context.flowLabel, route: { to: "/invite/diary/review", search } },
        { label: inviteCopy.organisationAndContact, route: { to: "/invite/diary/organisation", search } },
        { label: inviteCopy.addPatient, route: { to: "/invite/diary/patient", search } },
    ]);

    const hasTemplateSettings = search && (Boolean(search.trackers?.length) || Boolean(search.required_events?.length));
    const organisationId = typeof search.organisation_id === "string" ? search.organisation_id.trim() : "";
    const contactId = typeof search.contact_id === "string" ? search.contact_id.trim() : "";

    const quickstart: QuickstartDataSchemaT = useMemo(
        () =>
            ({
                ...normalizeTemplateSettingsSearch({
                    ...search,
                    quickstart_key: search.quickstart_key || "PAIN",
                    locale: search.locale ?? appLocale,
                }),
                patient_id: "",
            }) as QuickstartDataSchemaT,
        [search, appLocale]
    );

    return (
        <div className="space-y-4">
            {!isLoadingOrgs ? (
                <Stepper
                    steps={getDiaryInviteSteps("patient", {
                        organisationCount: organisations.length,
                        inviteMethod,
                        copy: inviteCopy,
                    })}
                />
            ) : null}

            {!hasTemplateSettings && (
                <Alert variant="destructive" className="bg-red-50 border-red-200">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                    <AlertDescription className="text-red-800">
                        {inviteCopy.noTemplateSelected}{" "}
                        <button
                            type="button"
                            onClick={() => void navigate({ to: "/templates" } as never)}
                            className="font-medium underline"
                        >
                            {inviteCopy.createTemplate}
                        </button>{" "}
                        {inviteCopy.createTemplateFirst}
                    </AlertDescription>
                </Alert>
            )}

            {organisationId && contactId ? (
                <InvitePatientForm quickstart={quickstart} />
            ) : !organisationId ? (
                <p className="text-muted-foreground text-sm">{inviteCopy.selectOrganisationToContinue}</p>
            ) : (
                <p className="text-muted-foreground text-sm">{inviteCopy.selectContactToContinue}</p>
            )}
        </div>
    );
}
